import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { colors } from '../../../../../colors';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../helpers/NumberFormatter';
import { Transaction } from '../../../../Overview/types';
import './index.css';
import TransactionItemCandlesChart from './Candles';
import CandleView from './CandleView';
import {
  CbrtRangeState,
  CompactTransaction,
  MrcState,
  ProfitResultIndicatorConfigs,
  WakeupTimes,
} from '../../../../ProfitCalculations/types';
import { Bot, BotTimeFrames } from '../../../types';

interface EnhancedProps {
  index: number;
  transaction: Transaction;
  compactTransactions: CompactTransaction[];
  compactTransactionIndex: number | null;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: Transaction[];
  showCandles: boolean;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  bot: Bot | undefined;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

interface Props {
  index: number;
  transaction: Transaction;
  compactTransactionIndex: number | null;
  compactTransactions: CompactTransaction[];
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: Transaction[];
  toggleCompactTransaction?: () => void;
  isCompact?: boolean;
  isVeryFirst?: boolean;
  isFirstClusteredTransaction?: boolean;
  clusteredTransactionIndex?: number;
  totalClusteredTransactions?: number;
  showCandles: boolean;
  isChartView: boolean;
  setIsChartView: React.Dispatch<React.SetStateAction<boolean>>;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  bot: Bot | undefined;
  indicatorConfig?: ProfitResultIndicatorConfigs;
}

const TransactionItem: React.FC<Props> = (props: Props) => {
  const {
    index,
    bot,
    transaction,
    compactTransactions,
    compactTransactionIndex,
    currentProfit,
    currentPriceChange,
    toggleCompactTransaction,
    isCompact = true,
    isVeryFirst = false,
    isFirstClusteredTransaction = false,
    totalClusteredTransactions = 0,
    clusteredTransactionIndex = 0,
    showCandles,
    isChartView,
    setIsChartView,
    cbrtRangeStates,
    mrcStates,
    timeFrames,
    showCurrentProfit,
    wakeupTimes,
    clusteredTransactions,
    indicatorConfig,
  } = props;

  const transactionOrderColor = useCallback(
    (orderType: 'BUY' | 'SELL') =>
      orderType === 'BUY' ? colors.green : colors.red,
    [],
  );

  const transactionSuccessColor = useCallback(
    (success: boolean | string | null) =>
      success === null
        ? colors.white
        : typeof success === 'string'
        ? colors.orange
        : success
        ? colors.green
        : colors.red,
    [],
  );

  const getProfitValue = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return profit;
    }

    if (profit === null) {
      return formatPercentage(0);
    }

    if (typeof profit === 'string') {
      return formatPercentage(parseFloat(profit));
    }

    return formatPercentage(profit);
  }, []);

  const getProfitColor = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return colors.orange;
    }

    if (profit === null) {
      return colors.white;
    }

    if (typeof profit === 'string') {
      return parseFloat(profit) >= 0 ? colors.green : colors.red;
    }

    return profit >= 0 ? colors.green : colors.red;
  }, []);

  const getTransactionType = useCallback((transactionType: string) => {
    if (transactionType.indexOf('REVERT') !== -1) {
      return 'REVERTED';
    }

    if (transactionType.indexOf('EARLIER') !== -1) {
      return 'Earlier Order';
    }

    if (transactionType.indexOf('MANUAL') !== -1) {
      return 'Manual';
    }

    return 'End Of Candle';
  }, []);

  const multiOrderIndex = useMemo(() => {
    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      return undefined;
    }

    if (isCompact) {
      return 'A';
    }

    return totalClusteredTransactions - clusteredTransactionIndex;
  }, [
    clusteredTransactionIndex,
    isCompact,
    totalClusteredTransactions,
    transaction.transactionType,
  ]);

  const multiOrderColor = useMemo(() => {
    if (isCompact) {
      return transactionSuccessColor(transaction.success);
    }

    const opacity =
      (totalClusteredTransactions - clusteredTransactionIndex) /
        totalClusteredTransactions +
      clusteredTransactionIndex / 40;

    if (transaction.success === null) {
      /** Use Order Color */
      if (transaction.orderType === 'BUY') {
        return `rgba(0, 176, 97, ${opacity})`;
      } else {
        return `rgba(240, 22, 47, ${opacity})`;
      }
    }

    /** Use success color */
    if (transaction.success) {
      return `rgba(0, 176, 97, ${opacity})`;
    } else {
      return `rgba(240, 22, 47, ${opacity})`;
    }
  }, [
    clusteredTransactionIndex,
    isCompact,
    totalClusteredTransactions,
    transaction,
    transactionSuccessColor,
  ]);

  const transactionLabel = useMemo(() => {
    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      return 'TP';
    }

    return typeof transaction.index !== 'undefined'
      ? parseInt(transaction.index, 10)
      : 'x';
  }, [transaction.index, transaction.transactionType]);

  const handleCandlesPress = useCallback(() => {
    setIsChartView(true);
  }, [setIsChartView]);

  const handleCloseChartView = useCallback(() => {
    setIsChartView(false);
  }, [setIsChartView]);

  const transactionPrice = useMemo(() => {
    const price = transaction.averageOrderPrice || transaction.orderPrice || 0;

    return formatPrice(price);
  }, [transaction]);

  return (
    <div
      className="detail-transactions-item-container"
      style={index === 0 && (isVeryFirst || isCompact) ? { marginTop: 0 } : {}}
    >
      <div className="detail-transactions-item-index-container">
        {(!!index || (!isVeryFirst && !isCompact)) && (
          <div className="detail-transactions-item-index-divider"></div>
        )}
        <div
          className="detail-transactions-item-index-circle"
          style={{
            borderColor: transactionOrderColor(transaction.orderType),
          }}
        >
          <span
            className="detail-transactions-item-index-text"
            style={{ fontSize: transactionLabel === 'TP' ? 32 : undefined }}
          >
            {transactionLabel}
            {multiOrderIndex && (
              <div
                className="detail-transactions-item-multi-index-circle"
                style={{
                  borderColor: multiOrderColor,
                }}
              >
                <span className="detail-transactions-item-multi-index-text">
                  {multiOrderIndex}
                </span>
              </div>
            )}
          </span>
        </div>

        {isChartView ? (
          <div className="detail-transactions-item-index-divider-extra-long"></div>
        ) : index === 0 ? (
          <div className="detail-transactions-item-index-divider"></div>
        ) : (
          <div className="detail-transactions-item-index-divider-small"></div>
        )}
      </div>

      <div
        className="detail-transactions-item-info-container-connector"
        style={transaction.type === 'profit' ? { width: '100%', flex: 1 } : {}}
      ></div>

      <div
        className="detail-transactions-item-info-container"
        style={
          index === 0 && (isVeryFirst || isCompact) ? { marginTop: 50 } : {}
        }
      >
        <div className="detail-transactions-item-info-container-date-container">
          <span className="detail-transactions-item-info-container-date-text">
            {moment(transaction.transactionTimestamp).format(
              'D MMM YYYY HH:mm',
            )}
          </span>
        </div>

        {showCurrentProfit &&
          compactTransactionIndex === 0 &&
          (isVeryFirst || isCompact) && (
            <div className="detail-transactions-item-info-container-header-container">
              <span className="detail-transactions-item-info-container-header-label">
                Current Profit:
              </span>
              <span
                className="detail-transactions-item-info-container-header-value"
                style={{
                  color: getProfitColor(currentProfit || 0),
                }}
              >
                {formatPercentage(currentProfit || 0)}
              </span>
              <span className="detail-transactions-item-info-container-header-label detail-transactions-item-info-container-header-left">
                Current Price Change:
              </span>
              <span
                className="detail-transactions-item-info-container-header-value"
                style={{
                  color: getProfitColor(currentProfit || 0),
                }}
              >
                {formatPercentage(currentPriceChange || 0)}
              </span>
            </div>
          )}

        {isChartView ? (
          <CandleView
            bot={bot}
            isCompact={isCompact}
            transaction={transaction}
            compactTransactions={compactTransactions}
            clusteredTransactions={clusteredTransactions}
            handleCloseChartView={handleCloseChartView}
            cbrtRangeStates={cbrtRangeStates}
            mrcStates={mrcStates}
            timeFrames={timeFrames}
            wakeupTimes={wakeupTimes}
            indicatorConfig={indicatorConfig}
          />
        ) : (
          <div className="detail-transactions-item-info-container-row">
            <div className="detail-transactions-item-info-container-column">
              {/** Left */}
              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label detail-transactions-item-info-container-value-bigger">
                  {isCompact ? 'Average Price:' : 'Price:'}
                </span>
                <span className="detail-transactions-item-info-container-value-text detail-transactions-item-info-container-value-bigger">
                  {transactionPrice}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Asset quantity:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.assetQuantity, false)}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container container-spacer">
                <span className="detail-transactions-item-info-container-value-label">
                  Asset Profit:
                </span>
                <span
                  className="detail-transactions-item-info-container-value-text"
                  style={{
                    color:
                      transaction.orderType === 'BUY'
                        ? getProfitColor(transaction.profit)
                        : colors.white,
                  }}
                >
                  {transaction.orderType === 'BUY'
                    ? getProfitValue(transaction.profit)
                    : '0%'}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Asset Balance:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.currentAssetBalance, false)}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Total Fee:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.totalFee)}
                </span>
              </div>
            </div>
            <div className="detail-transactions-item-info-container-column middle-container detail-transactions-item-info-container-middle-column">
              {/** Middle */}
              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Stable quantity:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.stableQuantity)}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container container-spacer">
                <span className="detail-transactions-item-info-container-value-label">
                  Stable Profit:
                </span>
                <span
                  className="detail-transactions-item-info-container-value-text"
                  style={{
                    color:
                      transaction.orderType === 'SELL'
                        ? getProfitColor(transaction.profit)
                        : colors.white,
                  }}
                >
                  {transaction.orderType === 'SELL'
                    ? getProfitValue(transaction.profit)
                    : '0%'}
                </span>
              </div>

              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Stable Balance:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.currentStableBalance)}
                </span>
              </div>
              <div className="detail-transactions-item-info-container-value-container">
                <span className="detail-transactions-item-info-container-value-label">
                  Fee Value:
                </span>
                <span className="detail-transactions-item-info-container-value-text">
                  {formatBalance(transaction.currentFee)}
                </span>
              </div>
            </div>
            {showCandles && (
              <div
                className="detail-transactions-item-info-container-column detail-transactions-item-candle-column"
                onClick={handleCandlesPress}
              >
                {/** Candles */}
                <TransactionItemCandlesChart
                  transaction={transaction}
                  compactTransactions={compactTransactions}
                />
              </div>
            )}
          </div>
        )}

        <div className="detail-transactions-item-info-container-see-all-container">
          <div className="detail-transactions-item-info-container-see-all-container-connector"></div>
          <span
            className="detail-transactions-item-info-container-see-all-container-text"
            onClick={toggleCompactTransaction}
          >
            {isCompact
              ? 'See all specific transactions'
              : 'See compact transaction'}
          </span>
        </div>
      </div>
    </div>
  );
};

const EnhancedTransactionItem: React.FC<EnhancedProps> = (
  props: EnhancedProps,
): any => {
  const { clusteredTransactions, compactTransactionIndex } = props;
  const [showCompactTransaction, setShowCompactTransaction] = useState(true);

  const [isChartView, setIsChartView] = useState(false);

  const toggleCompactTransaction = useCallback(() => {
    setShowCompactTransaction(!showCompactTransaction);
  }, [showCompactTransaction]);

  if (showCompactTransaction) {
    return (
      <TransactionItem
        {...props}
        toggleCompactTransaction={toggleCompactTransaction}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
      />
    );
  }

  if (clusteredTransactions.length) {
    return clusteredTransactions.map((t, index) => {
      return (
        <TransactionItem
          key={t.id}
          {...props}
          transaction={t}
          isCompact={false}
          isVeryFirst={compactTransactionIndex === 0 && index === 0}
          toggleCompactTransaction={toggleCompactTransaction}
          isFirstClusteredTransaction={index === 0}
          clusteredTransactionIndex={index}
          totalClusteredTransactions={clusteredTransactions.length}
          isChartView={isChartView}
          setIsChartView={setIsChartView}
        />
      );
    });
  }

  return (
    <TransactionItem
      {...props}
      toggleCompactTransaction={toggleCompactTransaction}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
    />
  );
};

export default React.memo(EnhancedTransactionItem);
